import React from 'react';

export default function ExpiryBarChart({ board }) {
  const [tooltip,  setTooltip]  = React.useState(null);
  const [startIdx, setStartIdx] = React.useState(0);
  const [dragOffset, setDragOffset] = React.useState(0);
  const dragRef    = React.useRef(null);
  const containerRef = React.useRef(null);
  const VISIBLE = 12;
  const TOTAL   = 36;
  const now     = new Date();

  const months = Array.from({ length: TOTAL }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    return { year: d.getFullYear(), month: d.getMonth() + 1,
             label: `${d.getMonth() + 1}월`,
             yearLabel: (i === 0 || d.getMonth() === 0) ? `'${String(d.getFullYear()).slice(2)}` : null };
  });
  const allItems = (board?.items || []).filter(x => !x.isSummary && x.expiry_date);
  const data = months.map(m => {
    const rooms = allItems.filter(x => {
      const d = new Date(x.expiry_date);
      return d.getFullYear() === m.year && d.getMonth() + 1 === m.month;
    });
    const sum = rooms.reduce((s, x) => s + (Number(String(x.deposit || '0').replace(/[^0-9]/g, '')) || 0), 0);
    return { ...m, sum, rooms };
  });
  const maxVal    = Math.max(...data.map(d => d.sum), 1);
  const BAR_H     = 38;
  const THRESHOLD = 150000000;

  const barWidth = containerRef.current ? (containerRef.current.clientWidth / VISIBLE) : 0;

  const onDragStart = (e) => {
    dragRef.current = { x: e.touches ? e.touches[0].clientX : e.clientX, startIdx };
    setDragOffset(0);
  };
  const onDragMove = (e) => {
    if (!dragRef.current) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setDragOffset(x - dragRef.current.x);
  };
  const onDragEnd = (e) => {
    if (!dragRef.current) return;
    const x = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const diff = dragRef.current.x - x;
    const steps = Math.round(diff / (barWidth || 28));
    setStartIdx(i => Math.min(Math.max(i + steps, 0), TOTAL - VISIBLE));
    setDragOffset(0);
    dragRef.current = null;
  };

  const translateX = -startIdx * (barWidth || 0) + dragOffset;

  return (
    <div style={{ position: 'relative' }}>
    <div ref={containerRef} style={{ overflow: 'hidden', cursor: 'grab', userSelect: 'none' }}
      onMouseDown={onDragStart} onMouseMove={onDragMove} onMouseUp={onDragEnd} onMouseLeave={onDragEnd}
      onTouchStart={onDragStart} onTouchMove={onDragMove} onTouchEnd={onDragEnd}
      onClick={() => setTooltip(null)}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: `${BAR_H + 40}px`,
                    width: `${TOTAL * 100 / VISIBLE}%`,
                    transform: `translateX(${translateX}px)`,
                    transition: dragRef.current ? 'none' : 'transform 0.2s ease' }}>
        {data.map((d, i) => {
          const barH   = d.sum > 0 ? Math.max((d.sum / maxVal) * BAR_H, 6) : 0;
          const eok    = Math.round(d.sum / 100000000 * 10) / 10;
          const isHigh = d.sum > THRESHOLD;
          const color  = isHigh ? '#f87171' : d.sum > 0 ? '#93c5fd' : 'rgba(255,255,255,0.1)';
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column',
                                  alignItems: 'center', justifyContent: 'flex-end', height: '100%',
                                  position: 'relative', cursor: d.sum > 0 ? 'pointer' : 'default' }}
              onClick={(e) => {
                if (!d.sum) return;
                e.stopPropagation();
                setTooltip(tooltip?.idx === i ? null : { idx: i, rooms: d.rooms, label: d.label, barI: i - startIdx });
              }}>
              {d.sum > 0 && (
                <div style={{ fontSize: '8px', color: '#fff', fontWeight: '700',
                              marginBottom: '2px', whiteSpace: 'nowrap' }}>
                  {eok}억
                </div>
              )}
              <div style={{ width: '100%', height: `${barH}px`, backgroundColor: color,
                            borderRadius: '4px 4px 0 0', minHeight: d.sum > 0 ? '6px' : '0' }} />
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.6)', marginTop: '2px' }}>
                {d.label}
              </div>
              <div style={{ fontSize: '8px', color: '#fbbf24', fontWeight: '700', height: '12px', marginTop: '1px' }}>
                {d.yearLabel || ''}
              </div>
            </div>
          );
        })}
      </div>
      {/* 말풍선 — overflow:hidden 밖에서 렌더링 */}
      {tooltip && (
        <div style={{ position: 'absolute', bottom: `${BAR_H + 28}px`,
                      left: `${(tooltip.barI + 0.5) * 100 / VISIBLE}%`,
                      transform: 'translateX(-50%)', backgroundColor: '#fff',
                      borderRadius: '8px', padding: '8px 10px', zIndex: 100,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.3)', minWidth: '130px',
                      pointerEvents: 'none' }}>
          <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#1e3a6e', marginBottom: '4px' }}>
            {tooltip.label} 만기 ({tooltip.rooms.length}세대)
          </div>
          {tooltip.rooms.map((r, ri) => {
            const yrs = (r.move_in_date && r.expiry_date)
              ? Math.round((new Date(r.expiry_date) - new Date(r.move_in_date)) / (1000*60*60*24*365))
              : null;
            const mRaw = Number(String(r.monthly_rent||'0').replace(/[^0-9]/g,''));
            const isJeonse = mRaw === 0; // 월세 0원(전세)만 다른 색으로 표시
            return (
              <div key={ri} style={{ fontSize: '10px', color: isJeonse ? '#0891b2' : '#374151', fontWeight: isJeonse ? 'bold' : 'normal', lineHeight: '1.6', whiteSpace: 'nowrap' }}>
                • {r.호수} {String(r.owner||'').replace('님','')} {Math.round(Number(String(r.deposit||'0').replace(/[^0-9]/g,''))/100000000*10)/10}억
                {(() => { const m = Math.round(Number(String(r.monthly_rent||'0').replace(/[^0-9]/g,''))/10000); return m > 0 ? <span style={{ color: '#6b7280' }}>/{m}</span> : null; })()}
                {yrs > 0 ? <span style={{ color: '#374151' }}> ({yrs})</span> : null}
                {r.expiry_date ? <span style={{ color: '#9ca3af' }}> {String(r.expiry_date).slice(8,10)}</span> : null}
              </div>
            );
          })}
          <div style={{ position: 'absolute', bottom: '-6px', left: '50%', transform: 'translateX(-50%)',
                        width: 0, height: 0, borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent', borderTop: '6px solid #fff' }} />
        </div>
      )}
    </div>
    </div>
  );
}
